package com.vizio.redwolf.pay.api

import com.vizio.redwolf.pay.model.CreateOrder
import com.vizio.redwolf.pay.model.CreateOrderInfo
import com.vizio.redwolf.pay.model.CreateTransactionInfo
import com.vizio.redwolf.pay.model.OrderPreview
import com.vizio.redwolf.pay.model.OrderPreviewInfo
import com.vizio.redwolf.pay.model.PartnerPlansInfo
import com.vizio.redwolf.pay.model.Plan
import com.vizio.redwolf.pay.model.PreviewSubscriptionInfo
import com.vizio.redwolf.pay.model.Subscription
import com.vizio.redwolf.pay.model.SubscriptionInvoice
import com.vizio.redwolf.pay.model.SubscriptionInvoiceSummary
import com.vizio.redwolf.pay.model.SubscriptionItemPreview
import com.vizio.redwolf.pay.model.SubscriptionResult
import com.vizio.redwolf.pay.model.TransactionOrder
import com.vizio.redwolf.pay.model.UpgradePlan
import com.vizio.redwolf.pay.model.V2GetInvoiceById
import com.vizio.redwolf.pay.model.V2GetInvoices
import com.vizio.redwolf.utils.model.VersionResponse

/**
 * Reference - https://lucid.app/lucidchart/9a50486b-e0fe-4422-92c0-e87af0dbfb5c/edit?page=k3Hp-ENlBbpM#
 */
interface SubscriptionServiceInterface {

    suspend fun previewSubscription(token: String, previewSubscriptionInfo: PreviewSubscriptionInfo, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<SubscriptionItemPreview>

    suspend fun purchaseSubscription(token: String, planCode: String, addOnCodes: List<String>? = null, couponCode: String? = null, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Subscription>

    suspend fun createTransaction(token: String, transactionInfo: CreateTransactionInfo, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<TransactionOrder>

    suspend fun createOrder(token: String, createOrderInfo: CreateOrderInfo, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<CreateOrder>

    suspend fun orderPreview(token: String, orderPreviewInfo: OrderPreviewInfo, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<OrderPreview>

    suspend fun getSubscriptions(token: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<List<Subscription>>

    /**
     * Gets a specific Subscription for a specific plan code
     *
     * @param token The ping access token
     * @param planCode The planCode for the desired subscription
     * @param sentryHeader The standardized sentry header used for tracking
     * @param tvAppId The standardized sentry header used for tracking
     * @return [SubscriptionResult] Returns a result that contains the standard subscription object
     */
    suspend fun getSubscriptionsWithPlanCode(token: String, planCode: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Subscription>

    suspend fun reactivateSubscription(token: String, subscriptionId: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Subscription>

    suspend fun cancelSubscription(token: String, subscriptionId: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Subscription>

    /**
     * Terminate subscription completely wipes/deletes the subscription for an account.
     * This should NOT be used interchangeably/confused with cancel subscription.
     * Clients should not use this request without a valid reason
     *
     * @param token The ping access token
     * @param subscriptionId The id of the subscription to be terminated
     * @param sentryHeader The standardized sentry header used for tracking
     * @param tvAppId The standardized sentry header used for tracking
     * @return [SubscriptionResult] Returns a result that contains the standard subscription object
     */
    suspend fun terminateSubscription(token: String, subscriptionId: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Subscription>

    suspend fun getV2Invoices(token: String, pageNumber: Int? = null, pageSize: Int? = null, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<V2GetInvoices>

    suspend fun getV2InvoiceById(token: String, invoiceId: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<V2GetInvoiceById>

    suspend fun getAccountInvoices(token: String, year: Int? = null, month: Int? = null, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<List<SubscriptionInvoiceSummary>>

    suspend fun getAccountInvoice(token: String, invoiceId: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<SubscriptionInvoice>

    suspend fun getPartnerIdPlans(partnerPlansInfo: PartnerPlansInfo, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<List<Plan>>

    suspend fun getPlan(planCode: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<Plan>

    suspend fun getPlanUpgrades(authToken: String, planCode: String, sentryHeader: String? = null, tvAppId: String? = null): SubscriptionResult<List<UpgradePlan>>

    suspend fun getVersion(): SubscriptionResult<VersionResponse>
}
